import React, { useState, useCallback, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { FileRestrictions, ValidationError, ValidationErrorType, FileWithPath } from '../../hooks/useS3Upload';
import {
  containsNativeFiles,
  getFilesFromDataTransferItems,
} from '../../utils/dataTransferEntries';
import { isFileTypeAccepted } from '../../utils/fileType';

// ===== Styled Components =====

/**
 * `align-items: center` は `.u-wrapper` 系（最大幅のみ指定で auto マージンを持たない）
 * の children を中央寄せするために必要。そのぶん幅指定のない children は内容幅まで
 * 縮むため、全幅に広げたい children 側で `width: 100%` を明示すること。
 */
const DropZoneContainer = styled.div`
  position: relative;
  width: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
`;

interface OverlayProps {
  $isDragActive: boolean;
}

const Overlay = styled.div<OverlayProps>`
  position: absolute;
  inset: 0;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  background-color: rgba(0, 154, 206, 0.1);
  border: 3px dashed var(--soremo-blue);
  border-radius: 8px;
  opacity: ${(props) => (props.$isDragActive ? 1 : 0)};
  visibility: ${(props) => (props.$isDragActive ? 'visible' : 'hidden')};
  transition: opacity 0.2s ease, visibility 0.2s ease;
  pointer-events: ${(props) => (props.$isDragActive ? 'auto' : 'none')};
`;

const OverlayIcon = styled.span.attrs({
  className: 'material-symbols-rounded',
})`
  font-size: 64px;
  color: var(--soremo-blue);
`;

const OverlayText = styled.p.attrs({
  className: 'bodytext',
})`
  color: var(--soremo-blue);
  text-align: center;
`;

// ===== Helper Functions =====

/** ファイルサイズをフォーマット */
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/** ファイルをバリデート */
const validateFiles = (
  files: File[],
  restrictions?: FileRestrictions
): { valid: File[]; errors: ValidationError[] } => {
  const valid: File[] = [];
  const errors: ValidationError[] = [];

  // maxFilesチェック（全体の数）
  if (restrictions?.maxFiles && files.length > restrictions.maxFiles) {
    // 超過分をエラーとして報告
    files.slice(restrictions.maxFiles).forEach((file) => {
      errors.push({
        type: 'too_many_files' as ValidationErrorType,
        file,
        message: `ファイル数が多すぎます（最大: ${restrictions.maxFiles}件）`,
      });
    });
    files = files.slice(0, restrictions.maxFiles);
  }

  for (const file of files) {
    // acceptチェック
    if (!isFileTypeAccepted(file, restrictions?.accept)) {
      errors.push({
        type: 'invalid_type' as ValidationErrorType,
        file,
        message: 'このファイル形式はサポートされていません',
      });
      continue;
    }

    // maxSizeチェック
    if (restrictions?.maxSize && file.size > restrictions.maxSize) {
      errors.push({
        type: 'file_too_large' as ValidationErrorType,
        file,
        message: `ファイルサイズが大きすぎます（最大: ${formatFileSize(restrictions.maxSize)}）`,
      });
      continue;
    }

    // minSizeチェック
    if (restrictions?.minSize && file.size < restrictions.minSize) {
      errors.push({
        type: 'file_too_small' as ValidationErrorType,
        file,
        message: `ファイルサイズが小さすぎます（最小: ${formatFileSize(restrictions.minSize)}）`,
      });
      continue;
    }

    valid.push(file);
  }

  return { valid, errors };
};

// ===== Props =====

export interface DropZoneOverlayProps {
  /** オーバーレイを有効にするかどうか */
  enabled?: boolean;
  /** ファイルがドロップされたときのコールバック */
  onDrop: (files: File[]) => void;
  /** ファイル制限設定 */
  restrictions?: FileRestrictions;
  /** バリデーションエラー時のコールバック */
  onValidationError?: (errors: ValidationError[]) => void;
  /** オーバーレイに表示するテキスト */
  overlayText?: string;
  /** ドラッグ中のオーバーレイに表示するテキスト */
  dragActiveText?: string;
  /** 子要素（ドロップ対象エリア） */
  children: React.ReactNode;
  /** フォルダドロップを有効にするかどうか */
  enableFolderDrop?: boolean;
  /** フォルダがドロップされたときのコールバック（フォルダパス情報付き） */
  onDropWithPath?: (filesWithPath: FileWithPath[]) => void;
}

// ===== Component =====

const DropZoneOverlay: React.FC<DropZoneOverlayProps> = ({
  enabled = true,
  onDrop,
  restrictions,
  onValidationError,
  overlayText = 'ファイルをドロップしてアップロード',
  dragActiveText,
  children,
  enableFolderDrop = false,
  onDropWithPath,
}) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const dragCounterRef = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // dragenterハンドラ
  const handleDragEnter = useCallback(
    (event: DragEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (!enabled || !containsNativeFiles(event)) return;

      dragCounterRef.current++;
      if (dragCounterRef.current === 1) {
        setIsDragActive(true);
      }
    },
    [enabled]
  );

  // dragleaveハンドラ
  const handleDragLeave = useCallback(
    (event: DragEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (!enabled) return;

      dragCounterRef.current--;
      if (dragCounterRef.current === 0) {
        setIsDragActive(false);
      }
    },
    [enabled]
  );

  // dragoverハンドラ
  const handleDragOver = useCallback(
    (event: DragEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (!enabled || !containsNativeFiles(event)) return;

      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'copy';
      }
    },
    [enabled]
  );

  // dropハンドラ
  const handleDrop = useCallback(
    async (event: DragEvent) => {
      event.preventDefault();
      event.stopPropagation();

      dragCounterRef.current = 0;
      setIsDragActive(false);

      if (!enabled || !event.dataTransfer) return;

      const validateAndDropPlainFiles = (files: File[]) => {
        if (files.length === 0) return;
        const { valid, errors } = validateFiles(files, restrictions);
        if (errors.length > 0) onValidationError?.(errors);
        if (valid.length > 0) onDrop(valid);
      };

      // dataTransfer は await を挟むと無効化されるため、フォールバック用の
      // File[] は entry 走査を始める前に同期的に確保しておく
      const plainFiles = Array.from(event.dataTransfer.files);

      // フォルダドロップが有効で、webkitGetAsEntryがサポートされている場合
      if (enableFolderDrop && event.dataTransfer.items && onDropWithPath) {
        try {
          const filesWithPath = await getFilesFromDataTransferItems(event.dataTransfer.items);
          if (filesWithPath.length === 0) {
            // entry 経由で 1 件も取れなかった場合でもドロップを捨てない
            validateAndDropPlainFiles(plainFiles);
            return;
          }

          const files = filesWithPath.map(f => f.file);
          const { valid, errors } = validateFiles(files, restrictions);
          if (errors.length > 0) onValidationError?.(errors);

          const validFileSet = new Set(valid);
          const validFilesWithPath = filesWithPath.filter(f => validFileSet.has(f.file));
          if (validFilesWithPath.length > 0) onDropWithPath(validFilesWithPath);
        } catch (error) {
          console.error('Failed to process folder drop:', error);
          validateAndDropPlainFiles(plainFiles);
        }
        return;
      }

      validateAndDropPlainFiles(plainFiles);
    },
    [enabled, restrictions, onValidationError, onDrop, enableFolderDrop, onDropWithPath]
  );

  // イベントリスナーの登録
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !enabled) return;

    container.addEventListener('dragenter', handleDragEnter);
    container.addEventListener('dragleave', handleDragLeave);
    container.addEventListener('dragover', handleDragOver);
    container.addEventListener('drop', handleDrop);

    return () => {
      container.removeEventListener('dragenter', handleDragEnter);
      container.removeEventListener('dragleave', handleDragLeave);
      container.removeEventListener('dragover', handleDragOver);
      container.removeEventListener('drop', handleDrop);
    };
  }, [enabled, handleDragEnter, handleDragLeave, handleDragOver, handleDrop]);

  // コンポーネントアンマウント時にdragCounterをリセット
  useEffect(() => {
    return () => {
      dragCounterRef.current = 0;
    };
  }, []);

  return (
    <DropZoneContainer ref={containerRef}>
      {children}
      <Overlay $isDragActive={isDragActive}>
        <OverlayIcon>file_upload</OverlayIcon>
        <OverlayText>
          {isDragActive && dragActiveText ? dragActiveText : overlayText}
        </OverlayText>
      </Overlay>
    </DropZoneContainer>
  );
};

export default DropZoneOverlay;
